import { ChevronDown } from "lucide-react";
import { useTranslation } from "react-i18next";

import type { useFieldOptionsFetcher } from "../api/useFieldOptionsFetcher";
import { VALUE_INPUT, type ValueInputKind } from "../conditions";

import { AsyncSelect, type AsyncSelectProps } from "@/components/ui/v2/async-select";
import { Input } from "@/components/ui/v2/input";
import { toSentenceCase } from "@/lib/helpers/common";
import { VacancyFieldOptionType } from "@/types/response/GetVacancyFieldOptionsResponse";

type ValueInputProps = ReturnType<typeof useFieldOptionsFetcher> &
  Pick<AsyncSelectProps<VacancyFieldOptionType>, "forceOpen" | "contentRef"> & {
  kind: Exclude<ValueInputKind, typeof VALUE_INPUT.NONE>;
  fieldLabel?: string;
  onChange: (values: string[]) => void;
  values: string[];
  "aria-invalid"?: boolean;
  "aria-describedby"?: string;
};

const ValueInput: React.FC<ValueInputProps> = ({
  kind,
  fieldLabel,
  onChange,
  values,
  paginatedFetcher,
  matchesFetcher,
  forceOpen,
  contentRef,
  "aria-invalid": ariaInvalid,
  "aria-describedby": ariaDescribedBy,
}) => {
  const { t } = useTranslation();

  const optionSelectProps = {
    paginatedFetcher,
    getOptionValue: (option: VacancyFieldOptionType) => String(option.value),
    getDisplayValue: (option: VacancyFieldOptionType) => option.label,
    renderOption: (option: VacancyFieldOptionType) => (
      <span className="truncate">{option.label}</span>
    ),
    width: "100%",
    contentWidth: "wide" as const,
    clearable: false,
    triggerIcon: ChevronDown,
    triggerVariant: "filled" as const,
    placeholder: toSentenceCase(
      t("placeholders.select", { entity: t("common.value") }),
    ),
    label: t("common.value"),
    "aria-invalid": ariaInvalid,
    "aria-describedby": ariaDescribedBy,
    forceOpen,
    contentRef,
  };

  if (kind === VALUE_INPUT.MULTI) {
    return (
      <AsyncSelect<VacancyFieldOptionType>
        {...optionSelectProps}
        multiple
        selectionControls={{ fetchMatches: matchesFetcher }}
        value={values}
        onChange={onChange}
      />
    );
  }

  return (
    <Input
      onChange={(e) => onChange(e.target.value ? [e.target.value] : [])}
      placeholder={toSentenceCase(
        t("placeholders.enter", { entity: t("common.value") }),
      )}
      type="text"
      value={values[0] ?? ""}
      aria-label={fieldLabel}
      aria-invalid={ariaInvalid}
      aria-describedby={ariaDescribedBy}
    />
  );
};

export default ValueInput;
